import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { SignedEdition } from "../../../../api/graphql";
import { SignedEditionListStore } from "../../store";
import { signedEditionDndaWorkflowStateDisplayId } from "../../../signedEditionDndaWorkflowState/store/SignedEditionDndaWorkflowStateCommonStore";
import { signedEditionSadaicWorkflowStateDisplayId } from "../../../signedEditionSadaicWorkflowState/store/SignedEditionSadaicWorkflowStateCommonStore";
import {
  signedEditionDndaWorkflow,
  signedEditionSadaicWorkflow,
} from "../../../../workflows/workflowDefinitions";
import type { WorkflowDefinition } from "../../../../common/controls/workflowRoadmapTypes";
import { musicalWorkDisplayId } from "../../../musicalWork/store/MusicalWorkCommonStore";
import { associatedDocumentSignedEditionDisplayId } from "../../../associatedDocumentSignedEdition/store/AssociatedDocumentSignedEditionCommonStore";
import { signedEditionBlockchainCertDisplayId } from "../../../signedEditionBlockchainCert/store/SignedEditionBlockchainCertCommonStore";
import { diaDeCalendario, formatearDia } from "../../../../common/calendarDate";

import { VisibilityIcon } from "../../../../icons";

export type GridExpressProps = { listStore?: SignedEditionListStore };

export const SignedEditionListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<SignedEdition>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: SignedEdition, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
      ],
      [langStore, store]
    );

    // Mismo criterio que el roadmap (concealErrors): el cliente del tenant no
    // opera /automation, así que un estado de FALLA se le muestra con el label
    // del hito en curso (ancla del workflow), nunca "Error …". El SysAdmin ve
    // el estado real.
    const isSysAdmin = !!rootStore.authStore.hasRole("SysAdmin");
    const concealedStateLabel = (
      stateId: string,
      workflow: WorkflowDefinition,
      labels: Record<string, string>,
      realLabel: string
    ): string => {
      const anchor = workflow.stateAnchors?.[stateId];
      if (!isSysAdmin && anchor?.kind === "error") {
        const toward = workflow.mainPath[anchor.towardIndex];
        return labels[toward] || realLabel;
      }
      return realLabel;
    };

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<SignedEdition>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "dndaState") {
          return {
            ...col,
            id: "dndaState",
            accessorFn: (row: SignedEdition) =>
              row.dndaState
                ? concealedStateLabel(
                    row.dndaState.id,
                    signedEditionDndaWorkflow,
                    langStore.SignedEditionDndaStateStateLabels,
                    signedEditionDndaWorkflowStateDisplayId(
                      row.dndaState,
                      langStore
                    )
                  )
                : "-",
          };
        }
        if (col.accessorKey === "sadaicState") {
          return {
            ...col,
            id: "sadaicState",
            accessorFn: (row: SignedEdition) =>
              row.sadaicState
                ? concealedStateLabel(
                    row.sadaicState.id,
                    signedEditionSadaicWorkflow,
                    langStore.SignedEditionSadaicStateStateLabels,
                    signedEditionSadaicWorkflowStateDisplayId(
                      row.sadaicState,
                      langStore
                    )
                  )
                : "-",
          };
        }
        if (col.accessorKey === "works") {
          return {
            ...col,
            id: "works",
            accessorFn: (row: SignedEdition) =>
              row.works?.length
                ? row.works
                    .map((x: any) => musicalWorkDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "publicationDate") {
          return {
            ...col,
            id: "publicationDate",
            accessorFn: (row: SignedEdition) =>
              formatearDia(
                diaDeCalendario(row.publicationDate),
                langStore.timeFormat
              ) || "-",
          };
        }
        if (col.accessorKey === "dndaDocuments") {
          return {
            ...col,
            id: "dndaDocuments",
            accessorFn: (row: SignedEdition) =>
              row.dndaDocuments?.length
                ? row.dndaDocuments
                    .map((x: any) =>
                      associatedDocumentSignedEditionDisplayId(x, langStore)
                    )
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "blockchainCertificates") {
          return {
            ...col,
            id: "blockchainCertificates",
            accessorFn: (row: SignedEdition) =>
              row.blockchainCertificates?.length
                ? row.blockchainCertificates
                    .map((x: any) =>
                      signedEditionBlockchainCertDisplayId(x, langStore)
                    )
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "dndaUploadKeyReceivedAt") {
          return {
            ...col,
            id: "dndaUploadKeyReceivedAt",
            accessorFn: (row: SignedEdition) =>
              row.dndaUploadKeyReceivedAt
                ? new Date(row.dndaUploadKeyReceivedAt).toLocaleString(
                    "es-ES",
                    {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }
                  )
                : "-",
          };
        }
        if (col.accessorKey === "dndaUploadKeyExpiresAt") {
          return {
            ...col,
            id: "dndaUploadKeyExpiresAt",
            accessorFn: (row: SignedEdition) =>
              row.dndaUploadKeyExpiresAt
                ? new Date(row.dndaUploadKeyExpiresAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "dndaComprobanteReceivedAt") {
          return {
            ...col,
            id: "dndaComprobanteReceivedAt",
            accessorFn: (row: SignedEdition) =>
              row.dndaComprobanteReceivedAt
                ? new Date(row.dndaComprobanteReceivedAt).toLocaleString(
                    "es-ES",
                    {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }
                  )
                : "-",
          };
        }
        if (col.accessorKey === "dndaLastComprobantePollAt") {
          return {
            ...col,
            id: "dndaLastComprobantePollAt",
            accessorFn: (row: SignedEdition) =>
              row.dndaLastComprobantePollAt
                ? new Date(row.dndaLastComprobantePollAt).toLocaleString(
                    "es-ES",
                    {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }
                  )
                : "-",
          };
        }
        if (col.accessorKey === "sadaicDeliveredAt") {
          return {
            ...col,
            id: "sadaicDeliveredAt",
            accessorFn: (row: SignedEdition) =>
              row.sadaicDeliveredAt
                ? new Date(row.sadaicDeliveredAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "createdAt_") {
          return {
            ...col,
            id: "createdAt_",
            accessorFn: (row: SignedEdition) =>
              row.createdAt_
                ? new Date(row.createdAt_).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "lastUpdatedAt_") {
          return {
            ...col,
            id: "lastUpdatedAt_",
            accessorFn: (row: SignedEdition) =>
              row.lastUpdatedAt_
                ? new Date(row.lastUpdatedAt_).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.signedEditionCurrentPage || [];

    return (
      <div id={"ListOfSignedEdition"}>
        <div id={"StartOfListingOfSignedEdition"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
